"use client";

import {
  KiteBrand,
  KiteBrandRequestDTO,
  KiteBrandService,
} from "@/services/KiteBrandService";
import GearItemsWithDialog from "./GearItemsWithDialog";

interface KiteBrandsProps {
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  initialLimit?: number;
  onLimitChange?: (limit: number) => void;
}

const KiteBrands = ({
  initialSearch = "",
  onSearchChange,
  initialPage = 1,
  onPageChange,
  initialLimit = 5,
  onLimitChange,
}: KiteBrandsProps = {}) => {
  // Define columns for the brands table
  const columns = [
    {
      field: "brand",
      header: "Brand Name",
      sortable: true,
    },
    {
      field: "modelsCount",
      header: "Models",
      sortable: true,
      render: (item: KiteBrand) => item.modelsCount || 0,
    },
  ];

  // Define form fields for adding/editing brands
  const formFields = [
    {
      name: "brand",
      label: "Brand Name",
      type: "text" as const,
      required: true,
    },
  ];

  // Function to create RequestDTO from form data
  const createBrandRequestDto = (
    formData: Record<string, any>
  ): KiteBrandRequestDTO => {
    return {
      brand: formData.brand,
    };
  };

  // Function to determine if a brand can be deleted
  const canDeleteBrand = (brand: KiteBrand) => {
    const modelsCount = brand.modelsCount || 0;
    return {
      can: modelsCount === 0,
      reason:
        modelsCount > 0
          ? "Cannot delete brand with associated models"
          : undefined,
    };
  };

  return (
    <GearItemsWithDialog<KiteBrand, KiteBrandRequestDTO>
      title="Manage Brands"
      service={{
        getItems: (limit, sortField, sortOrder, page, search) =>
          KiteBrandService.getBrands(limit, sortField, sortOrder, page, search),
        createItem: (data) => KiteBrandService.createBrand(data),
        updateItem: (id, data) => KiteBrandService.updateBrand(id, data),
        deleteItem: (id) => KiteBrandService.deleteBrand(id),
      }}
      columns={columns}
      formFields={formFields}
      itemTypeNameSingular="Brand"
      itemTypeNamePlural="brands"
      requestDtoFactory={createBrandRequestDto}
      canDeleteItem={canDeleteBrand}
      initialSearch={initialSearch}
      onSearchChange={onSearchChange}
      initialPage={initialPage}
      onPageChange={onPageChange}
      initialLimit={initialLimit}
      onLimitChange={onLimitChange}
    />
  );
};

export default KiteBrands;
